import React, { useEffect, useState, useRef } from "react";
import { MessageAnimationContext } from "../messageAnimations";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  alpha: number;
  color: string;
  size: number;
  trail: { x: number; y: number }[];
}

const FragFireworksAnimation = (
  context: MessageAnimationContext
): React.ReactElement => {
  const { message, localConnectionId, gameState, onComplete, playAudio } = context;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [showMessage, setShowMessage] = useState(false);
  const [messageOpacity, setMessageOpacity] = useState(0);
  const [fontSize, setFontSize] = useState(48);
  const animationRef = useRef<number | undefined>(undefined);
  const particlesRef = useRef<Particle[]>([]);
  const isCompleteRef = useRef<boolean>(false);
  const hasStartedRef = useRef<boolean>(false);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    if (message.type !== "frag") {
      onComplete();
      return;
    }

    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      hasStartedRef.current = false;
      return;
    }

    const getViewportSize = () => ({
      width: window.innerWidth || document.documentElement.clientWidth,
      height: window.innerHeight || document.documentElement.clientHeight,
    });

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const { width, height } = getViewportSize();

      const setStyle = (prop: string, value: string) => {
        canvas.style.setProperty(prop, value, "important");
      };

      setStyle("position", "fixed");
      setStyle("top", "0");
      setStyle("left", "0");
      setStyle("width", `${width}px`);
      setStyle("height", `${height}px`);
      setStyle("max-width", `${width}px`);
      setStyle("max-height", `${height}px`);
      setStyle("min-width", `${width}px`);
      setStyle("min-height", `${height}px`);
      setStyle("margin", "0");
      setStyle("padding", "0");
      setStyle("box-sizing", "border-box");

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };

    requestAnimationFrame(() => {
      resizeCanvas();
      requestAnimationFrame(resizeCanvas);
    });

    window.addEventListener("resize", resizeCanvas);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", resizeCanvas);
      window.visualViewport.addEventListener("scroll", resizeCanvas);
    }

    const calculateFontSize = () => {
      const { width, height } = getViewportSize();
      const minDimension = Math.min(width, height);
      return Math.max(32, Math.min(72, minDimension * 0.05));
    };
    setFontSize(calculateFontSize());

    const colors = [
      "#ff0844",
      "#ffb100",
      "#00e0ff",
      "#6a0dad",
      "#00ff88",
      "#ff4500",
    ];

    const isMobile = window.innerWidth < 768;
    const createExplosion = (x: number, y: number) => {
      const particleCount = isMobile ? 50 : 100;
      const velocity = isMobile ? 2 + Math.random() * 3 : 3 + Math.random() * 4;
      const size = isMobile ? 2 : 3;

      for (let i = 0; i < particleCount; i++) {
        const angle = (Math.PI * 2 * i) / particleCount;
        particlesRef.current.push({
          x,
          y,
          vx: Math.cos(angle) * velocity,
          vy: Math.sin(angle) * velocity,
          alpha: 1,
          color: colors[Math.floor(Math.random() * colors.length)],
          size,
          trail: [],
        });
      }
    };

    startTimeRef.current = Date.now();
    isCompleteRef.current = false;

    let audioHandle = playAudio?.() ?? null;
    const audioTimer = window.setTimeout(() => {
      if (!audioHandle) {
        audioHandle = playAudio?.() ?? null;
      }
    }, 250);

    const scheduleFirework = () => {
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed < 2000) {
        const { width, height } = getViewportSize();
        const x = Math.random() * width;
        const y = Math.random() * height * 0.6 + height * 0.1;
        createExplosion(x, y);
        const nextInterval = isMobile
          ? 400 + Math.random() * 400
          : 300 + Math.random() * 500;
        setTimeout(scheduleFirework, nextInterval);
      }
    };

    scheduleFirework();

    const drawParticle = (p: Particle) => {
      p.trail.push({ x: p.x, y: p.y });
      if (p.trail.length > 10) p.trail.shift();

      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.1;
      p.alpha -= 0.01;

      if (p.alpha <= 0) return false;

      p.trail.forEach((trailPoint, j) => {
        const trailAlpha = (j / p.trail.length) * p.alpha * 0.5;
        ctx.save();
        ctx.globalAlpha = trailAlpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(trailPoint.x, trailPoint.y, p.size * 0.7, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      ctx.save();
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      return true;
    };

    const updateMessageOpacity = (elapsed: number) => {
      if (elapsed >= 2000 && !showMessage) {
        setShowMessage(true);
      }

      if (elapsed >= 2000 && elapsed < 2300) {
        setMessageOpacity(Math.min(1, (elapsed - 2000) / 300));
      } else if (elapsed >= 2300 && elapsed < 4000) {
        setMessageOpacity(1);
      } else if (elapsed >= 4000 && elapsed < 4500) {
        setMessageOpacity(Math.max(0, 1 - (elapsed - 4000) / 500));
      } else if (elapsed >= 4500) {
        setMessageOpacity(0);
        isCompleteRef.current = true;
        onComplete();
        return false;
      }
      return true;
    };

    const animate = () => {
      if (isCompleteRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      const { width, height } = getViewportSize();

      ctx.clearRect(0, 0, width, height);

      for (let i = particlesRef.current.length - 1; i >= 0; i--) {
        if (!drawParticle(particlesRef.current[i])) {
          particlesRef.current.splice(i, 1);
        }
      }

      if (!updateMessageOpacity(elapsed)) return;

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      isCompleteRef.current = true;
      hasStartedRef.current = false;
      window.removeEventListener("resize", resizeCanvas);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", resizeCanvas);
        window.visualViewport.removeEventListener("scroll", resizeCanvas);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      clearTimeout(audioTimer);
      audioHandle?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (message.type !== "frag") {
    return <></>;
  }

  // Resolve fragger and fragged players from gameState using numPlayer
  let fragger: { connectionId: string; name: string } | undefined;
  let fragged: { connectionId: string; name: string } | undefined;

    for (const [, player] of gameState.players.entries()) {
      if (player.numPlayer === message.fraggerNumPlayer) {
        fragger = {
          connectionId: player.connectionId,
          name: player.name || "Unknown",
        };
      }
      if (player.numPlayer === message.fraggedNumPlayer) {
        fragged = {
          connectionId: player.connectionId,
          name: player.name || "Unknown",
        };
      }
    }

  if (!fragger || !fragged) {
    console.warn(`[FragFireworksAnimation] Could not resolve players: fraggerNumPlayer=${message.fraggerNumPlayer}, fraggedNumPlayer=${message.fraggedNumPlayer}`);
    onComplete();
    return <></>;
  }

  const isLocalPlayer = fragger.connectionId === localConnectionId;
  const messageText = isLocalPlayer
    ? `Fragged ${fragged.name}!`
    : `${fragger.name} fragged you!`;

  return (
    <>
      <style>{`
        .fireworks-canvas {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          background: transparent !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          filter: none !important;
          border: none !important;
          box-shadow: none !important;
          opacity: 1 !important;
          margin: 0 !important;
          padding: 0 !important;
          z-index: 9999 !important;
        }
        .fireworks-message-text {
          position: fixed;
          top: 40%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-weight: 900;
          color: #FFFFFF;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.8), 0 2px 8px rgba(0, 0, 0, 0.9);
          letter-spacing: 3px;
          text-transform: uppercase;
          pointer-events: none;
          z-index: 2001;
          word-wrap: break-word;
          white-space: normal;
          padding: 0 20px;
          font-family: Impact, 'Arial Black', 'Franklin Gothic Bold', Arial, sans-serif;
          line-height: 1.1;
          user-select: none;
        }
      `}</style>

      <canvas
        ref={canvasRef}
        className="fireworks-canvas"
        style={{
          pointerEvents: "none",
          imageRendering: "crisp-edges",
          willChange: "contents",
        }}
      />

      {showMessage && (
        <div
          className="fireworks-message-text"
          style={{
            opacity: messageOpacity,
            fontSize: `${fontSize}px`,
            maxWidth: `${Math.min(window.innerWidth * 0.9, 800)}px`,
            textAlign: "center",
          }}
        >
          {messageText}
        </div>
      )}
    </>
  );
};

export default FragFireworksAnimation;
